/**
 * Per-entry trust state machine (shadow phase).
 *
 * Pure, dependency-free — mirrors the row-level lifecycle machine in
 * apps/api/src/workflows/cache/lifecycle.ts, at compiled-entry granularity.
 * In the shadow phase nothing reads `trust` for routing; writers annotate it
 * and telemetry observes it so the enforce phase (prefix/remainder routing)
 * can be judged against real soak data.
 *
 * State diagram (per entry):
 *
 *   unproven --attested×N--> verified
 *   suspect  --attested----> verified      (one clean re-proof, like the row)
 *   any      --implicated--> unproven      (content was replaced)
 *   any      --defect------> suspect       (content indicted, not replaced)
 *   any      --soft_failure> (unchanged; softFailures++)
 *
 * The one hard invariant: an entry never holds MORE trust than its row.
 * Only a verified row may hold verified entries; every write clamps via
 * `clampEntryTrustToRow`. The clamp demotes state but PRESERVES streak —
 * streak is evidence, not trust, and is exactly the "would-be verified"
 * signal the enforce phase consumes.
 */

import type { EntryTrust, EntryTrustState, StoredCompiledEntry } from './types.js';
import { sortObjectKeys } from './node-hash.js';

/**
 * Row playbook states, mirrored from the row lifecycle machine
 * (apps/api/src/workflows/cache/lifecycle.ts). Kept as a literal union here
 * because this package must stay dependency-free.
 */
export type RowPlaybookState = 'unverified' | 'verified' | 'suspect' | 'retired';

/** Attestations required to consider an entry verified. Mirrors PLAYBOOK_VERIFY_STREAK. */
export const ENTRY_VERIFY_STREAK = 2;

export type EntryTrustEvent =
  /** This exact content ran verbatim and worked (before any L2 involvement). */
  | 'attested'
  /** The entry's content was replaced (healed / re-authored) — trust resets. */
  | 'implicated'
  /** The entry's content failed and was indicted but NOT replaced. */
  | 'defect'
  /**
   * A transient failure (timing_hidden / verdict `transient`) survived without
   * a content change. Bookkeeping only in the shadow phase — demote-at-N is an
   * explicit enforce-phase decision.
   */
  | 'soft_failure';

const TRUST_RANK: Record<EntryTrustState, number> = {
  suspect: 0,
  unproven: 1,
  verified: 2,
};

/** Highest entry-trust state a row in `rowState` may hold. */
export function entryTrustCeiling(rowState: RowPlaybookState): EntryTrustState {
  return rowState === 'verified' ? 'verified' : 'unproven';
}

/**
 * Lazy on-touch derivation for entries persisted before the trust field
 * existed (no migration, no backfill): the entry inherits what its row's
 * state already implies. A verified row's entries were all proven together —
 * they start at the row's streak. Anything else starts unproven (or suspect,
 * matching a suspect row's standing).
 */
export function deriveEntryTrustFromRow(
  rowState: RowPlaybookState,
  rowStreak: number,
  now: string
): EntryTrust {
  if (rowState === 'verified') {
    return {
      state: 'verified',
      streak: Math.max(rowStreak, ENTRY_VERIFY_STREAK),
      softFailures: 0,
      updatedAt: now,
    };
  }
  if (rowState === 'suspect') {
    return { state: 'suspect', streak: 0, softFailures: 0, updatedAt: now };
  }
  return { state: 'unproven', streak: 0, softFailures: 0, updatedAt: now };
}

/**
 * Apply one trust event. Pure and clamp-free — callers clamp the result
 * against the row via `clampEntryTrustToRow` before persisting.
 *
 * `verifyStreak` defaults to ENTRY_VERIFY_STREAK; asserted nodes may pass 1,
 * mirroring the row machine's PLAYBOOK_VERIFY_STREAK_ASSERTED.
 */
export function applyEntryTrustEvent(
  current: EntryTrust,
  event: EntryTrustEvent,
  now: string,
  verifyStreak: number = ENTRY_VERIFY_STREAK
): EntryTrust {
  switch (event) {
    case 'attested': {
      const streak = current.streak + 1;
      const promoted = streak >= verifyStreak || current.state === 'suspect';
      return {
        state: promoted ? 'verified' : current.state === 'verified' ? 'verified' : 'unproven',
        streak,
        // A suspect entry that re-proves starts a clean slate; promotion to
        // verified clears the transient-failure tally too.
        softFailures: promoted ? 0 : current.softFailures,
        updatedAt: now,
      };
    }
    case 'implicated':
      return { state: 'unproven', streak: 0, softFailures: 0, updatedAt: now };
    case 'defect':
      return { state: 'suspect', streak: 0, softFailures: current.softFailures, updatedAt: now };
    case 'soft_failure':
      return { ...current, softFailures: current.softFailures + 1, updatedAt: now };
  }
}

/**
 * The hard monotonicity invariant: demote any entry trusted above its row's
 * ceiling. Streak and softFailures are PRESERVED (evidence, not trust) and
 * `updatedAt` is untouched — a clamp is a constraint, not an event.
 */
export function clampEntryTrustToRow(trust: EntryTrust, rowState: RowPlaybookState): EntryTrust {
  const ceiling = entryTrustCeiling(rowState);
  if (TRUST_RANK[trust.state] <= TRUST_RANK[ceiling]) return trust;
  return { ...trust, state: 'unproven' };
}

/**
 * Array-level form of `clampEntryTrustToRow`, for write paths that must
 * clamp a whole `compiled_entries_json` array against the row state being
 * persisted (not some stale row state read earlier in the request). Gap
 * entries and cached entries with no `trust` pass through untouched — this
 * helper only clamps trust that already exists; it never derives trust.
 * Returns a new array; the input is never mutated.
 */
export function clampEntriesToRow(
  entries: StoredCompiledEntry[],
  rowState: RowPlaybookState
): { entries: StoredCompiledEntry[]; clampedCount: number } {
  let clampedCount = 0;
  const clamped = entries.map((entry) => {
    if (entry.type !== 'cached' || !entry.trust) return entry;
    const nextTrust = clampEntryTrustToRow(entry.trust, rowState);
    if (nextTrust.state !== entry.trust.state) clampedCount += 1;
    return { ...entry, trust: nextTrust };
  });
  return { entries: clamped, clampedCount };
}

/**
 * Content-identity trust carry: trust rides with entry content.
 *
 * For each cached entry in `nextEntries`, if the incumbent has a cached entry
 * for the same stepOrder whose referenced actions are structurally identical
 * (ignoring only the positional `index` field), the incumbent's `trust`
 * carries onto the new entry unchanged. Any content difference — or any
 * resolution failure — leaves the entry fresh (no trust field), which lazy
 * derivation treats as unproven.
 *
 * Deliberately false-negative-safe: an unnecessary reset loses evidence but
 * can never over-trust; equality is strict structural identity, not semantic
 * similarity.
 */
export function carryEntryTrustByContent(args: {
  nextEntries: StoredCompiledEntry[];
  nextActions: ReadonlyArray<unknown>;
  priorEntries: StoredCompiledEntry[] | null | undefined;
  priorActions: ReadonlyArray<unknown> | null | undefined;
}): StoredCompiledEntry[] {
  const identicalOrders = contentIdenticalOrders(args);
  const priorByOrder = new Map<number, StoredCompiledEntry>();
  for (const entry of args.priorEntries ?? []) {
    priorByOrder.set(entry.stepOrder, entry);
  }

  return args.nextEntries.map((entry) => {
    if (entry.type !== 'cached' || !identicalOrders.has(entry.stepOrder)) return entry;
    const prior = priorByOrder.get(entry.stepOrder);
    if (!prior || prior.type !== 'cached' || !prior.trust) return entry;
    return { ...entry, trust: prior.trust };
  });
}

/**
 * Step orders whose cached entries resolve to the same structural action
 * content. Positional action `index` values do not participate in identity;
 * missing entries, gaps, null actions, and invalid action positions never
 * compare identical.
 */
export function contentIdenticalOrders(args: {
  nextEntries: StoredCompiledEntry[];
  nextActions: ReadonlyArray<unknown>;
  priorEntries: StoredCompiledEntry[] | null | undefined;
  priorActions: ReadonlyArray<unknown> | null | undefined;
}): Set<number> {
  const priorByOrder = new Map<number, StoredCompiledEntry>();
  for (const entry of args.priorEntries ?? []) {
    priorByOrder.set(entry.stepOrder, entry);
  }

  const identicalOrders = new Set<number>();
  for (const entry of args.nextEntries) {
    if (entry.type !== 'cached') continue;
    const prior = priorByOrder.get(entry.stepOrder);
    if (!prior || prior.type !== 'cached') continue;

    const nextContent = resolveContentSignature(entry.actionIndices, args.nextActions);
    const priorContent = resolveContentSignature(prior.actionIndices, args.priorActions ?? []);
    if (nextContent !== null && priorContent !== null && nextContent === priorContent) {
      identicalOrders.add(entry.stepOrder);
    }
  }
  return identicalOrders;
}

/** Stable signature of an entry's referenced actions, positional index excluded. */
function resolveContentSignature(
  actionIndices: number[],
  actions: ReadonlyArray<unknown>
): string | null {
  const resolved: unknown[] = [];
  for (const index of actionIndices) {
    const action = actions[index];
    if (action === null || typeof action !== 'object') return null;
    const { index: _positional, ...content } = action as Record<string, unknown>;
    resolved.push(sortObjectKeys(content));
  }
  return JSON.stringify(resolved);
}
